import { useState } from 'react';
import { ArrowPathIcon, ArrowUpIcon, PencilSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';
import type { CoworkQueuedMessage } from '../../types/cowork';
import { coworkService } from '../../services/cowork';
import { i18nService } from '../../services/i18n';

/** 单条队列项维护编辑草稿；服务端失败时保留输入，不乐观删除待确认引导。 */
export default function CoworkQueuedMessageItem({ item, index }: { item: CoworkQueuedMessage; index: number }) {
  const [draft, setDraft] = useState<{ text: string; revision: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const t = i18nService.t.bind(i18nService);
  const waiting = Boolean(item.steering);
  /** 操作期间避免重复点击，错误由当前条目展示。 */
  const run = async (operation: () => Promise<void>, closeEditor = false) => {
    if (busy) return;
    setBusy(true); setError('');
    try { await operation(); if (closeEditor) setDraft(null); }
    catch { setError(t('coworkQueuedUpdateFailed')); }
    finally { setBusy(false); }
  };
  return <div className="rounded-lg px-2.5 py-2 text-xs dark:bg-claude-darkBg/70 bg-claude-bg">
    <div className="flex items-center gap-2">
      <span className="shrink-0 opacity-60">{index + 1}</span>
      <span className="min-w-0 flex-1 truncate" title={item.text ?? item.preview}>{item.preview || '…'}</span>
      {!draft && !waiting && <button type="button" disabled={busy || item.text === undefined}
        className="rounded-md p-1 hover:text-claude-accent disabled:opacity-40" title={t('coworkQueuedEdit')} aria-label={t('coworkQueuedEdit')}
        onClick={() => { setDraft({ text: item.text ?? '', revision: item.revision ?? 0 }); setError(''); }}>
        <PencilSquareIcon className="h-4 w-4" />
      </button>}
      {!draft && <button type="button" disabled={busy || (!waiting && !item.canSteer)}
        className="flex shrink-0 items-center gap-1 rounded-md p-1 text-claude-accent disabled:opacity-40"
        title={t(waiting ? 'coworkQueuedCheck' : item.canSteer ? 'coworkQueuedSteer' : item.hasAttachments ? 'coworkQueuedSteerAttachments' : 'coworkQueuedSteerUnavailable')}
        onClick={() => void run(() => coworkService.steerQueuedMessage(item.sessionId, item.id, item.revision ?? 0))}>
        {waiting ? <ArrowPathIcon className={`h-4 w-4 ${busy ? 'animate-spin motion-reduce:animate-none' : ''}`} /> : <ArrowUpIcon className="h-4 w-4" />}
        {t(waiting ? 'coworkQueuedCheck' : 'coworkQueuedSteer')}
      </button>}
      <button type="button" disabled={busy || waiting} className="rounded-md p-1 text-red-500 disabled:opacity-30"
        onClick={() => void run(() => coworkService.cancelQueuedMessage(item.sessionId, item.id))}
        title={t('coworkQueuedCancel')} aria-label={t('coworkQueuedCancel')}><XMarkIcon className="h-4 w-4" /></button>
    </div>
    {draft && <div className="mt-2 space-y-2">
      <textarea autoFocus rows={3} maxLength={200000} value={draft.text} disabled={busy || waiting}
        aria-label={t('coworkQueuedEdit')} className="w-full resize-y rounded-lg border border-claude-border bg-transparent p-2 text-sm outline-none focus:border-claude-accent dark:border-claude-darkBorder"
        onChange={event => setDraft({ ...draft, text: event.target.value })} />
      <div className="flex justify-end gap-3">
        <button type="button" disabled={busy} onClick={() => { setDraft(null); setError(''); }}>{t('cancel')}</button>
        <button type="button" disabled={busy || waiting || !draft.text.trim()} className="text-claude-accent disabled:opacity-40"
          onClick={() => void run(() => coworkService.editQueuedMessage(item.sessionId, item.id, draft.revision, draft.text), true)}>{t('save')}</button>
      </div>
    </div>}
    {waiting && <p role="status" className="mt-1 text-claude-textSecondary dark:text-claude-darkTextSecondary">
      {t(item.steering === 'unknown' ? 'coworkQueuedSteeringUnknown' : 'coworkQueuedSteeringPending')}
    </p>}
    {error && <p role="alert" className="mt-1 text-red-500">{error}</p>}
  </div>;
}
